import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Zap, Search, User, Briefcase, Building2, MapPin, DollarSign, 
  Clock, TrendingUp, Loader2, ChevronRight, Star, Target,
  ArrowRight, Filter, RefreshCw, Play, Pause, XCircle, Eye,
  ExternalLink, CheckCircle, AlertCircle, Globe, Sparkles,
  Mail, Phone, Linkedin, Share2, Users, Send, History, RotateCcw,
  ArrowUpRight, FileUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIJobSearchPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState('search'); // 'search' | 'extracts' | 'logs'
  
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [jobResults, setJobResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('match');
  const [showHidden, setShowHidden] = useState(false);
  const [sharingHR, setSharingHR] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareMinScore, setShareMinScore] = useState(60);
  const [hrContacts, setHrContacts] = useState([]);
  
  // Share history state
  const [showShareHistory, setShowShareHistory] = useState(false);
  const [shareHistory, setShareHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedShares, setSelectedShares] = useState([]);
  const [resending, setResending] = useState(false);
  
  // Promote to ATS state
  const [promotingJob, setPromotingJob] = useState(null);
  const [promotedJobs, setPromotedJobs] = useState({});
  
  // Apollo Extracts state
  const [apolloExtracts, setApolloExtracts] = useState([]);
  const [apolloLogs, setApolloLogs] = useState([]);
  const [extractsLoading, setExtractsLoading] = useState(false);
  const [extractsStats, setExtractsStats] = useState({});
  const [selectedExtracts, setSelectedExtracts] = useState([]);
  const [extractsPage, setExtractsPage] = useState(1);
  const [extractsTotal, setExtractsTotal] = useState(0);
  const [extractsSearch, setExtractsSearch] = useState('');
  const [extractsFilter, setExtractsFilter] = useState('all'); // all, with_email, emailed, new
  const [showBulkSaveModal, setShowBulkSaveModal] = useState(false);
  const [bulkSaveData, setBulkSaveData] = useState('');
  const [bulkSaveName, setBulkSaveName] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkEmailing, setBulkEmailing] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadCandidates();
  }, []);

  // Load Apollo extracts when tab changes
  useEffect(() => {
    if (activeTab === 'extracts') {
      loadApolloExtracts();
    } else if (activeTab === 'logs') {
      loadApolloLogs();
    }
  }, [activeTab, extractsPage, extractsFilter]);

  const loadApolloExtracts = async () => {
    setExtractsLoading(true);
    const token = localStorage.getItem('ats_token');
    try {
      let url = `${API_URL}/api/apollo/extracts?page=${extractsPage}&limit=50`;
      if (extractsFilter === 'with_email') url += '&has_email=true';
      if (extractsFilter === 'new') url += '&status=new';
      if (extractsSearch) url += `&search=${encodeURIComponent(extractsSearch)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApolloExtracts(data.extracts || []);
        setExtractsTotal(data.total || 0);
        setExtractsStats(data.stats || {});
      }
    } catch (error) {
      toast.error('Failed to load Apollo extracts');
    } finally {
      setExtractsLoading(false);
    }
  };

  const loadApolloLogs = async () => {
    setExtractsLoading(true);
    const token = localStorage.getItem('ats_token');
    try {
      const response = await fetch(`${API_URL}/api/apollo/extracts/logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApolloLogs(data.logs || []);
      }
    } catch (error) {
      toast.error('Failed to load extraction logs');
    } finally {
      setExtractsLoading(false);
    }
  };

  const handleBulkSave = async () => {
    if (!bulkSaveData.trim()) {
      toast.error('Please paste Apollo data');
      return;
    }
    
    setBulkSaving(true);
    const token = localStorage.getItem('ats_token');
    
    try {
      // Try to parse JSON data
      let contacts;
      try {
        const parsed = JSON.parse(bulkSaveData);
        contacts = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        // Try CSV-like format (comma or tab separated)
        toast.error('Please paste valid JSON data from Apollo');
        setBulkSaving(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/apollo/extracts/bulk-save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contacts,
          extraction_name: bulkSaveName || `Import ${new Date().toLocaleDateString()}`,
          source: 'apollo_extension'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Saved ${data.total_saved} contacts!`);
        setShowBulkSaveModal(false);
        setBulkSaveData('');
        setBulkSaveName('');
        loadApolloExtracts();
        loadApolloLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save Apollo data');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkEmail = async () => {
    if (selectedExtracts.length === 0) {
      toast.error('Select contacts to email');
      return;
    }
    
    setBulkEmailing(true);
    const token = localStorage.getItem('ats_token');
    
    try {
      const response = await fetch(`${API_URL}/api/apollo/extracts/bulk-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          extract_ids: selectedExtracts,
          subject: 'Job Opportunity'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Emailed ${data.sent} contacts!`);
        setSelectedExtracts([]);
        loadApolloExtracts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send emails');
      }
    } catch (error) {
      toast.error('Failed to send bulk emails');
    } finally {
      setBulkEmailing(false);
    }
  };

  const handleExportExtracts = async () => {
    const token = localStorage.getItem('ats_token');
    try {
      const response = await fetch(`${API_URL}/api/apollo/extracts/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `apollo_extracts_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('Export downloaded!');
      }
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const toggleExtractSelection = (id) => {
    setSelectedExtracts(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllExtracts = () => {
    const withEmail = apolloExtracts.filter(e => e.email);
    if (selectedExtracts.length === withEmail.length) {
      setSelectedExtracts([]);
    } else {
      setSelectedExtracts(withEmail.map(e => e.id));
    }
  };

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/candidates?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates || []);
      }
    } catch (error) {
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadJobResults = async (candidateId, includeHidden = false) => {
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/results/${candidateId}?include_hidden=${includeHidden}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setJobResults(data.results || []);
      }
    } catch (error) {
      console.error('Failed to load job results:', error);
    }
  };

  const loadSearchStatus = async (candidateId) => {
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/candidate/${candidateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.searches && data.searches.length > 0) {
          setSearchStatus(data.searches[0]);
        } else {
          setSearchStatus(null);
        }
      }
    } catch (error) {
      console.error('Failed to load search status:', error);
    }
  };

  const selectCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    setJobResults([]);
    setSearchStatus(null);
    setHrContacts([]);
    await Promise.all([
      loadJobResults(candidate.id, showHidden),
      loadSearchStatus(candidate.id),
      loadHRContacts(candidate.id)
    ]);
  };

  const triggerSearch = async () => {
    if (!selectedCandidate) return;
    
    setSearching(true);
    try {
      const token = localStorage.getItem('ats_token');
      console.log('Triggering search for candidate:', selectedCandidate.id);
      
      const response = await fetch(
        `${API_URL}/api/ai-job-search/trigger/${selectedCandidate.id}`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      console.log('Response status:', response.status);
      
      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      if (response.ok) {
        const data = await response.json();
        console.log('Search started:', data);
        toast.success('AI Job Search started! This may take 2-3 minutes.');
        pollSearchStatus(selectedCandidate.id);
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to start search';
        try {
          const errorData = await responseClone.json();
          errorMessage = errorData.detail || errorMessage;
          console.error('Error detail:', errorData);
        } catch {
          try {
            const text = await response.text();
            errorMessage = text || response.statusText || errorMessage;
          } catch {
            errorMessage = response.statusText || errorMessage;
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('AI Job Search error:', error);
      toast.error(error.message || 'Failed to trigger search');
    } finally {
      setSearching(false);
    }
  };

  const pollSearchStatus = async (candidateId) => {
    const poll = async () => {
      await loadSearchStatus(candidateId);
      await loadJobResults(candidateId, showHidden);
      
      // Check if still in progress
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/candidate/${candidateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.searches && data.searches.length > 0) {
          const status = data.searches[0].status;
          if (['pending', 'generating_queries', 'searching', 'enriching', 'matching'].includes(status)) {
            setTimeout(poll, 5000); // Poll every 5 seconds
          } else if (status === 'completed') {
            toast.success('Job search completed!');
          } else if (status === 'failed') {
            toast.error('Job search failed. Check settings.');
          }
        }
      }
    };
    
    setTimeout(poll, 3000);
  };

  const forceVisible = async (resultId) => {
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/force-visible/${resultId}`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        toast.success('Job now visible to candidate');
        loadJobResults(selectedCandidate.id, showHidden);
      }
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  // Load HR contacts for selected candidate
  const loadHRContacts = async (candidateId) => {
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/hr-contacts/${candidateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setHrContacts(data.hr_contacts || []);
      }
    } catch (error) {
      console.error('Failed to load HR contacts:', error);
    }
  };

  // Share single job with HR
  const shareWithHR = async (resultId) => {
    if (!selectedCandidate) return;
    
    setSharingHR(true);
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/share-with-hr/${selectedCandidate.id}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            result_ids: [resultId],
            min_score: 0,
            include_without_email: false
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.shared_count > 0) {
          toast.success(`Shared with HR: ${data.shared_details[0]?.hr_email}`);
          loadHRContacts(selectedCandidate.id);
        } else {
          toast.error('No HR contact available for this job');
        }
      } else {
        let errorMessage = 'Failed to share';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Failed to share with HR');
    } finally {
      setSharingHR(false);
    }
  };

  // Bulk share with HR
  const bulkShareWithHR = async (forceReshare = false) => {
    if (!selectedCandidate) return;
    
    setSharingHR(true);
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/share-with-hr/${selectedCandidate.id}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            result_ids: [],
            min_score: shareMinScore,
            include_without_email: false,
            force_reshare: forceReshare
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.shared_count > 0) {
          toast.success(`${forceReshare ? 'Reshared' : 'Shared'} ${data.shared_count} job matches with HR contacts`);
          loadHRContacts(selectedCandidate.id);
        } else {
          toast.info('No jobs to share (no HR contacts found)');
        }
        setShowShareDialog(false);
      } else {
        let errorMessage = 'Failed to share';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Failed to share with HR');
    } finally {
      setSharingHR(false);
    }
  };

  // Load share history for current candidate
  const loadShareHistory = async () => {
    if (!selectedCandidate) return;
    
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(
        `${API_URL}/api/ai-job-search/share-history/${selectedCandidate.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setShareHistory(data.shares || []);
      }
    } catch (error) {
      console.error('Failed to load share history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Resend single email
  const resendEmail = async (shareToken) => {
    const token = localStorage.getItem('ats_token');
    try {
      const response = await fetch(
        `${API_URL}/api/ai-job-search/resend-email/${shareToken}`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Email resent to ${data.review_link ? 'HR' : 'recipient'}`);
        loadShareHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to resend');
      }
    } catch (error) {
      toast.error('Failed to resend email');
    }
  };

  // Bulk resend emails
  const bulkResendEmails = async () => {
    if (selectedShares.length === 0) {
      toast.error('Please select emails to resend');
      return;
    }
    
    setResending(true);
    const token = localStorage.getItem('ats_token');
    let successCount = 0;
    let failCount = 0;
    
    for (const shareToken of selectedShares) {
      try {
        const response = await fetch(
          `${API_URL}/api/ai-job-search/resend-email/${shareToken}`,
          { 
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (e) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Resent ${successCount} email${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to resend ${failCount} email${failCount > 1 ? 's' : ''}`);
    }
    
    setSelectedShares([]);
    loadShareHistory();
    setResending(false);
  };

  // Toggle share selection
  const toggleShareSelection = (token) => {
    setSelectedShares(prev => 
      prev.includes(token) 
        ? prev.filter(t => t !== token)
        : [...prev, token]
    );
  };

  // Select all shares
  const selectAllShares = () => {
    if (selectedShares.length === shareHistory.length) {
      setSelectedShares([]);
    } else {
      setSelectedShares(shareHistory.map(s => s.token));
    }
  };

  // Promote job to ATS
  const promoteToATS = async (resultId) => {
    setPromotingJob(resultId);
    const token = localStorage.getItem('ats_token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/ai-job-search/promote-to-ats/${resultId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            create_company: true,
            auto_match: true
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPromotedJobs(prev => ({ ...prev, [resultId]: data.job_id }));
        
        if (data.already_exists) {
          toast.info('This job was already promoted to ATS', {
            description: `Job ID: ${data.job_id}`
          });
        } else {
          toast.success('Job promoted to ATS!', {
            description: `${data.job?.title} at ${data.job?.company_name}`,
            action: {
              label: 'View Job',
              onClick: () => window.open(`/ats/jobs/${data.job_id}`, '_blank')
            }
          });
        }
        
        // Reload job results to update the promoted status
        loadJobResults(selectedCandidate.id, showHidden);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to promote job');
      }
    } catch (error) {
      console.error('Failed to promote job:', error);
      toast.error('Failed to promote job to ATS');
    } finally {
      setPromotingJob(null);
    }
  };

  // Count jobs with HR contacts
  const jobsWithHR = jobResults.filter(j => j.hr_contact?.hr_email).length;
  const jobsSharedWithHR = hrContacts.filter(c => c.already_shared).length;

  const filteredCandidates = candidates.filter(c => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(searchLower) ||
      c.current_designation?.toLowerCase().includes(searchLower) ||
      c.technical_skills?.some(s => s.toLowerCase().includes(searchLower))
    );
  });

  const sortedJobs = [...jobResults].sort((a, b) => {
    switch (sortBy) {
      case 'match': return (b.match_score || 0) - (a.match_score || 0);
      case 'company': return (a.company_name || '').localeCompare(b.company_name || '');
      case 'recent': return new Date(b.created_at) - new Date(a.created_at);
      default: return 0;
    }
  });

  const getMatchColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-amber-600 bg-amber-100';
    if (score >= 50) return 'text-blue-600 bg-blue-100';
    return 'text-slate-600 bg-slate-100';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-slate-100 text-slate-600', icon: Clock, label: 'Pending' },
      generating_queries: { color: 'bg-blue-100 text-blue-600', icon: Sparkles, label: 'Generating Queries' },
      searching: { color: 'bg-violet-100 text-violet-600', icon: Globe, label: 'Searching Jobs' },
      enriching: { color: 'bg-amber-100 text-amber-600', icon: Zap, label: 'Enriching Data' },
      matching: { color: 'bg-purple-100 text-purple-600', icon: Target, label: 'Matching' },
      completed: { color: 'bg-green-100 text-green-600', icon: CheckCircle, label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-600', icon: AlertCircle, label: 'Failed' },
      paused: { color: 'bg-amber-100 text-amber-600', icon: Pause, label: 'Paused' },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const visibleCount = jobResults.filter(j => j.visible).length;
  const hiddenCount = jobResults.filter(j => !j.visible).length;

  return (
    <div className="p-6" data-testid="ai-job-search-page">
      <Header 
        title="AI Job Search Engine" 
        subtitle="Search external job boards and match candidates with opportunities worldwide"
      />

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'search' 
              ? 'border-violet-600 text-violet-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-search"
        >
          <Search className="w-4 h-4 inline mr-2" />
          Job Search
        </button>
        <button
          onClick={() => setActiveTab('extracts')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'extracts' 
              ? 'border-violet-600 text-violet-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-extracts"
        >
          <Users className="w-4 h-4 inline mr-2" />
          Apollo Extracts
          {extractsStats.total_contacts > 0 && (
            <Badge variant="secondary" className="ml-2">{extractsStats.total_contacts}</Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'logs' 
              ? 'border-violet-600 text-violet-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-logs"
        >
          <History className="w-4 h-4 inline mr-2" />
          Raw Logs
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'search' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Candidate Selection Panel */}
          <div className="lg:col-span-1">
            <Card className="h-[calc(100vh-280px)] flex flex-col">
              <CardContent className="p-4 border-b">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-slate-800">Select Candidate</h3>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search candidates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="candidate-search-input"
                />
              </div>
            </CardContent>
            
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : filteredCandidates.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No candidates found</p>
              ) : (
                <div className="space-y-2">
                  {filteredCandidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      onClick={() => selectCandidate(candidate)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedCandidate?.id === candidate.id
                          ? 'bg-violet-100 border-2 border-violet-400'
                          : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                      }`}
                      data-testid={`candidate-item-${candidate.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{candidate.name}</p>
                          <p className="text-sm text-slate-600 truncate">{candidate.current_designation}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            {candidate.total_years_experience && (
                              <span>{candidate.total_years_experience} yrs exp</span>
                            )}
                            {candidate.current_location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {candidate.current_location}
                              </span>
                            )}
                          </div>
                          {candidate.technical_skills?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {candidate.technical_skills.slice(0, 3).map((skill, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs py-0">
                                  {skill}
                                </Badge>
                              ))}
                              {candidate.technical_skills.length > 3 && (
                                <Badge variant="outline" className="text-xs py-0">
                                  +{candidate.technical_skills.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Job Results Panel */}
        <div className="lg:col-span-2">
          <Card className="h-[calc(100vh-200px)] flex flex-col">
            <CardContent className="p-4 border-b">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-slate-800">
                    {selectedCandidate 
                      ? `External Jobs for ${selectedCandidate.name}` 
                      : 'Select a Candidate'}
                  </h3>
                  {jobResults.length > 0 && (
                    <div className="flex gap-2">
                      <Badge className="bg-green-100 text-green-700">
                        {visibleCount} visible
                      </Badge>
                      {hiddenCount > 0 && (
                        <Badge className="bg-slate-100 text-slate-600">
                          {hiddenCount} hidden
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {selectedCandidate && (
                    <>
                      <Button
                        onClick={triggerSearch}
                        disabled={searching || (searchStatus?.status && ['searching', 'enriching', 'matching', 'generating_queries'].includes(searchStatus.status))}
                        className="bg-violet-600 hover:bg-violet-700"
                        data-testid="trigger-search-btn"
                      >
                        {searching ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
                        ) : (
                          <><Zap className="w-4 h-4 mr-2" /> Search Jobs</>
                        )}
                      </Button>
                      
                      {jobsWithHR > 0 && (
                        <>
                          <Button
                            variant="outline"
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => setShowShareDialog(true)}
                            data-testid="bulk-share-hr-btn"
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share with HR ({jobsWithHR})
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowShareHistory(true)}
                            data-testid="share-history-btn"
                          >
                            <History className="w-4 h-4 mr-1" />
                            Sent Emails
                          </Button>
                        </>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowHidden(!showHidden);
                          if (selectedCandidate) {
                            loadJobResults(selectedCandidate.id, !showHidden);
                          }
                        }}
                        data-testid="toggle-hidden-btn"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {showHidden ? 'Hide Low Matches' : 'Show All'}
                      </Button>
                    </>
                  )}
                  
                  {jobResults.length > 0 && (
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-36 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="match">Match Score</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="recent">Most Recent</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              
              {/* Search Status */}
              {searchStatus && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Search Status:</span>
                    {getStatusBadge(searchStatus.status)}
                  </div>
                  {searchStatus.progress && (
                    <>
                      <Progress 
                        value={(searchStatus.progress.completed_queries / (searchStatus.progress.total_queries || 1)) * 100} 
                        className="h-2 mb-2"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Queries: {searchStatus.progress.completed_queries}/{searchStatus.progress.total_queries}</span>
                        <span>Jobs Found: {searchStatus.progress.total_jobs_found}</span>
                        <span>Matched: {searchStatus.progress.jobs_matched}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>

            <div className="flex-1 overflow-y-auto p-4">
              {!selectedCandidate ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <Target className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg">Select a candidate to find external jobs</p>
                  <p className="text-sm mt-2">AI will search Google Jobs, LinkedIn, Indeed & more</p>
                </div>
              ) : searching || ['searching', 'enriching', 'matching', 'generating_queries'].includes(searchStatus?.status) ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative">
                    <Zap className="w-12 h-12 text-violet-600 animate-pulse" />
                  </div>
                  <p className="mt-4 text-slate-600">Searching external job boards...</p>
                  <p className="text-sm text-slate-400">This may take 2-3 minutes</p>
                </div>
              ) : jobResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <Briefcase className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg">No job results yet</p>
                  <p className="text-sm mt-2">Click "Search Jobs" to find matching positions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedJobs.map((job, idx) => (
                    <Card 
                      key={job.id} 
                      className={`overflow-hidden hover:shadow-md transition-shadow ${!job.visible ? 'opacity-60 border-dashed' : ''}`}
                      data-testid={`job-result-${job.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Rank Badge */}
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            job.match_score >= 80 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                            job.match_score >= 60 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                            job.match_score >= 50 ? 'bg-gradient-to-br from-blue-500 to-indigo-600' :
                            'bg-gradient-to-br from-slate-400 to-slate-500'
                          }`}>
                            #{idx + 1}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="text-lg font-semibold text-slate-800">
                                    {job.job_title}
                                  </h4>
                                  {!job.visible && (
                                    <Badge variant="outline" className="text-xs text-slate-500">
                                      Hidden from candidate
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                                  <Building2 className="w-4 h-4" />
                                  <span>{job.company_name}</span>
                                  {job.location && (
                                    <>
                                      <MapPin className="w-4 h-4 ml-2" />
                                      <span>{job.location}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Score */}
                              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getMatchColor(job.match_score)}`}>
                                {Math.round(job.match_score || 0)}% Match
                              </div>
                            </div>

                            {/* Description */}
                            {job.description && (
                              <p className="mt-2 text-sm text-slate-600 line-clamp-3">
                                {job.description}
                              </p>
                            )}

                            {/* Job Details Row */}
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              {job.salary && (
                                <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
                                  <DollarSign className="w-3 h-3 mr-1" />
                                  {job.salary}
                                </Badge>
                              )}
                              {job.job_type && (
                                <Badge variant="outline" className="text-xs">
                                  {job.job_type}
                                </Badge>
                              )}
                              {job.work_from_home && (
                                <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                                  Remote/WFH
                                </Badge>
                              )}
                              {job.posted_at && (
                                <Badge variant="outline" className="text-xs text-slate-500">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {job.posted_at}
                                </Badge>
                              )}
                              {job.experience_required && (
                                <Badge variant="outline" className="text-xs">
                                  {job.experience_required}
                                </Badge>
                              )}
                            </div>

                            {/* Requirements */}
                            {job.requirements?.length > 0 && (
                              <div className="mt-2">
                                <span className="text-xs font-medium text-slate-500">Requirements: </span>
                                <span className="text-xs text-slate-600">
                                  {job.requirements.slice(0, 3).join(' | ')}
                                  {job.requirements.length > 3 && ` +${job.requirements.length - 3} more`}
                                </span>
                              </div>
                            )}

                            {/* Benefits */}
                            {job.benefits?.length > 0 && (
                              <div className="mt-1">
                                <span className="text-xs font-medium text-emerald-600">Benefits: </span>
                                <span className="text-xs text-slate-600">
                                  {job.benefits.slice(0, 3).join(' | ')}
                                  {job.benefits.length > 3 && ` +${job.benefits.length - 3} more`}
                                </span>
                              </div>
                            )}

                            {/* Score Breakdown */}
                            {job.match_breakdown && (
                              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
                                {Object.entries({
                                  'Skills': job.match_breakdown.skills_match,
                                  'Experience': job.match_breakdown.experience_match,
                                  'Location': job.match_breakdown.location_match,
                                  'Seniority': job.match_breakdown.seniority_match,
                                  'Industry': job.match_breakdown.industry_match,
                                  'Education': job.match_breakdown.education_match,
                                }).map(([label, score]) => (
                                  <div key={label} className="bg-slate-50 rounded-lg p-2 text-center">
                                    <div className="text-xs text-slate-500">{label}</div>
                                    <div className="font-semibold text-slate-800">{Math.round(score || 0)}%</div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Skill Gaps & Strengths */}
                            {job.match_breakdown?.skill_gaps?.length > 0 && (
                              <div className="mt-3">
                                <span className="text-xs text-red-500 font-medium">Skill Gaps: </span>
                                <span className="text-xs text-slate-600">
                                  {job.match_breakdown.skill_gaps.slice(0, 5).join(', ')}
                                </span>
                              </div>
                            )}
                            {job.match_breakdown?.strengths?.length > 0 && (
                              <div className="mt-1">
                                <span className="text-xs text-green-500 font-medium">Strengths: </span>
                                <span className="text-xs text-slate-600">
                                  {job.match_breakdown.strengths.slice(0, 5).join(', ')}
                                </span>
                              </div>
                            )}

                            {/* HR Contact Info */}
                            {job.hr_contact?.hr_email && (
                              <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                                <div className="flex items-center gap-2 text-sm text-emerald-700 mb-1">
                                  <Users className="w-4 h-4" />
                                  <span className="font-medium">HR Contact Found</span>
                                </div>
                                <div className="text-sm text-slate-600">
                                  <p className="font-medium">{job.hr_contact.hr_name}</p>
                                  {job.hr_contact.hr_title && (
                                    <p className="text-xs text-slate-500">{job.hr_contact.hr_title}</p>
                                  )}
                                  <div className="flex items-center gap-3 mt-1">
                                    <span className="flex items-center gap-1 text-xs">
                                      <Mail className="w-3 h-3" />
                                      {job.hr_contact.hr_email}
                                    </span>
                                    {job.hr_contact.hr_linkedin && (
                                      <a 
                                        href={job.hr_contact.hr_linkedin} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                      >
                                        <Linkedin className="w-3 h-3" />
                                        LinkedIn
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center flex-wrap gap-2 mt-4 pt-3 border-t">
                              {/* Promote to ATS Button */}
                              <Button
                                size="sm"
                                variant={job.promoted_to_job_id || promotedJobs[job.id] ? "secondary" : "default"}
                                className={job.promoted_to_job_id || promotedJobs[job.id] 
                                  ? "bg-green-100 text-green-700 hover:bg-green-200" 
                                  : "bg-blue-600 hover:bg-blue-700"}
                                onClick={() => promoteToATS(job.id)}
                                disabled={promotingJob === job.id || job.promoted_to_job_id || promotedJobs[job.id]}
                                data-testid={`promote-ats-${job.id}`}
                              >
                                {promotingJob === job.id ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : job.promoted_to_job_id || promotedJobs[job.id] ? (
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                ) : (
                                  <ArrowUpRight className="w-4 h-4 mr-1" />
                                )}
                                {job.promoted_to_job_id || promotedJobs[job.id] ? 'In ATS' : 'Promote to ATS'}
                              </Button>
                              
                              {job.apply_link && (
                                <Button size="sm" variant="outline" asChild>
                                  <a href={job.apply_link} target="_blank" rel="noopener noreferrer">
                                    View Job <ExternalLink className="w-4 h-4 ml-1" />
                                  </a>
                                </Button>
                              )}
                              {job.hr_contact?.hr_email && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => shareWithHR(job.id)}
                                  disabled={sharingHR || hrContacts.find(c => c.result_id === job.id)?.already_shared}
                                  data-testid={`share-hr-${job.id}`}
                                >
                                  {hrContacts.find(c => c.result_id === job.id)?.already_shared ? (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Shared
                                    </>
                                  ) : (
                                    <>
                                      <Share2 className="w-4 h-4 mr-1" />
                                      Share with HR
                                    </>
                                  )}
                                </Button>
                              )}
                              {!job.visible && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => forceVisible(job.id)}
                                  data-testid={`force-visible-${job.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Make Visible
                                </Button>
                              )}
                              <Badge variant="outline" className="text-xs">
                                via {job.source}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
      )}

      {/* Apollo Extracts Tab */}
      {activeTab === 'extracts' && (
        <div className="space-y-4">
          {/* Stats & Actions */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="text-base px-3 py-1">
                Total: {extractsStats.total_contacts || 0}
              </Badge>
              <Badge variant="secondary" className="text-base px-3 py-1">
                With Email: {extractsStats.with_email || 0}
              </Badge>
              <Badge className="bg-green-100 text-green-700 text-base px-3 py-1">
                Emailed: {extractsStats.emailed || 0}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setShowBulkSaveModal(true)}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="bulk-save-btn"
              >
                <FileUp className="w-4 h-4 mr-2" />
                Bulk Save Apollo Data
              </Button>
              <Button 
                onClick={handleBulkEmail}
                disabled={selectedExtracts.length === 0 || bulkEmailing}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="bulk-email-btn"
              >
                {bulkEmailing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Email Selected ({selectedExtracts.length})
              </Button>
              <Button onClick={handleExportExtracts} variant="outline" data-testid="export-btn">
                <ExternalLink className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, company..."
                value={extractsSearch}
                onChange={(e) => setExtractsSearch(e.target.value)}
                className="pl-10"
                onKeyDown={(e) => e.key === 'Enter' && loadApolloExtracts()}
              />
            </div>
            <Select value={extractsFilter} onValueChange={setExtractsFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contacts</SelectItem>
                <SelectItem value="with_email">With Email</SelectItem>
                <SelectItem value="new">New Only</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={loadApolloExtracts}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Select All */}
          {apolloExtracts.filter(e => e.email).length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={selectedExtracts.length === apolloExtracts.filter(e => e.email).length}
                onCheckedChange={selectAllExtracts}
              />
              <span className="text-slate-600">
                Select all with email ({apolloExtracts.filter(e => e.email).length})
              </span>
            </div>
          )}

          {/* Extracts Table */}
          {extractsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : apolloExtracts.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Apollo Extracts Yet</h3>
              <p className="text-sm text-slate-500 mt-2">
                Click "Bulk Save Apollo Data" to import contacts from Apollo.io
              </p>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase w-10"></th>
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Title</th>
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {apolloExtracts.map((extract) => (
                      <tr key={extract.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          {extract.email && (
                            <Checkbox
                              checked={selectedExtracts.includes(extract.id)}
                              onCheckedChange={() => toggleExtractSelection(extract.id)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">{extract.name || '-'}</div>
                          {extract.linkedin && (
                            <a href={extract.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                              LinkedIn
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{extract.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{extract.title || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{extract.company_name || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {extract.emailed && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Emailed</Badge>
                            )}
                            {extract.converted_to_job_id && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Converted</Badge>
                            )}
                            {!extract.emailed && !extract.converted_to_job_id && (
                              <Badge variant="outline" className="text-xs">{extract.status || 'new'}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {extract.email && !extract.emailed && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600">
                                <Mail className="w-3 h-3" />
                              </Button>
                            )}
                            {extract.phone && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600">
                                <Phone className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {extractsTotal > 50 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-slate-500">
                    Page {extractsPage} of {Math.ceil(extractsTotal / 50)}
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      disabled={extractsPage === 1}
                      onClick={() => setExtractsPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      disabled={extractsPage >= Math.ceil(extractsTotal / 50)}
                      onClick={() => setExtractsPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Raw Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-800">Extraction History</h3>
            <Button variant="ghost" onClick={loadApolloLogs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {extractsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : apolloLogs.length === 0 ? (
            <Card className="p-12 text-center">
              <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Extraction Logs</h3>
              <p className="text-sm text-slate-500 mt-2">
                Logs will appear here when you save Apollo data
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {apolloLogs.map((log) => (
                <Card key={log.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-slate-800">{log.name}</h4>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span>{log.total_contacts} contacts</span>
                        <span>•</span>
                        <span>{log.source}</span>
                        <span>•</span>
                        <span>{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                      {log.notes && (
                        <p className="mt-2 text-sm text-slate-600">{log.notes}</p>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setSelectedLog(selectedLog === log.id ? null : log.id)}
                    >
                      {selectedLog === log.id ? 'Hide' : 'View'} Raw Data
                    </Button>
                  </div>
                  {selectedLog === log.id && log.raw_log && (
                    <pre className="mt-4 p-4 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto max-h-64">
                      {JSON.stringify(log.raw_log, null, 2)}
                    </pre>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bulk Save Modal */}
      <Dialog open={showBulkSaveModal} onOpenChange={setShowBulkSaveModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-violet-600" />
              Bulk Save Apollo Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Extraction Name (optional)
              </label>
              <Input
                placeholder="e.g., HR Managers - Tech Companies"
                value={bulkSaveName}
                onChange={(e) => setBulkSaveName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Paste Apollo JSON Data
              </label>
              <textarea
                className="w-full h-64 p-3 border rounded-lg font-mono text-sm"
                placeholder='Paste your Apollo contact data here as JSON array, e.g.:\n[\n  {"name": "John Doe", "email": "john@company.com", ...},\n  ...\n]'
                value={bulkSaveData}
                onChange={(e) => setBulkSaveData(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-2">
                Export contacts from Apollo.io and paste the JSON data here
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkSaveModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkSave}
              disabled={bulkSaving || !bulkSaveData.trim()}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {bulkSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
              Save Contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-600" />
              Share with HR Contacts
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Send {selectedCandidate?.name}'s profile to HR contacts found during job search.
            </p>
            
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Jobs with HR contacts:</span>
                <span className="font-medium">{jobsWithHR}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Already shared:</span>
                <span className="font-medium text-emerald-600">{jobsSharedWithHR}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Remaining to share:</span>
                <span className="font-medium text-blue-600">{jobsWithHR - jobsSharedWithHR}</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Minimum Match Score
              </label>
              <Select value={String(shareMinScore)} onValueChange={(v) => setShareMinScore(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50% and above</SelectItem>
                  <SelectItem value="60">60% and above</SelectItem>
                  <SelectItem value="70">70% and above</SelectItem>
                  <SelectItem value="80">80% and above</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Only share jobs where match score meets this threshold
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
            {jobsSharedWithHR > 0 && (
              <Button 
                variant="outline"
                onClick={() => bulkShareWithHR(true)} 
                disabled={sharingHR}
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                {sharingHR ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resharing...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Force Reshare All ({jobsWithHR})
                  </>
                )}
              </Button>
            )}
            <Button 
              onClick={() => bulkShareWithHR(false)} 
              disabled={sharingHR || jobsWithHR - jobsSharedWithHR === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {sharingHR ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sharing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Share New ({jobsWithHR - jobsSharedWithHR})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share History Dialog */}
      <Dialog open={showShareHistory} onOpenChange={(open) => {
        setShowShareHistory(open);
        if (open) loadShareHistory();
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Sent Emails History
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : shareHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No emails sent yet for this candidate</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Select All */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={selectedShares.length === shareHistory.length && shareHistory.length > 0}
                      onCheckedChange={selectAllShares}
                    />
                    <span className="text-sm font-medium">
                      Select All ({shareHistory.length})
                    </span>
                  </label>
                  {selectedShares.length > 0 && (
                    <Button 
                      size="sm" 
                      onClick={bulkResendEmails}
                      disabled={resending}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {resending ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4 mr-1" />
                      )}
                      Resend Selected ({selectedShares.length})
                    </Button>
                  )}
                </div>

                {/* Email List */}
                {shareHistory.map((share) => (
                  <div 
                    key={share.token} 
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50"
                  >
                    <Checkbox 
                      checked={selectedShares.includes(share.token)}
                      onCheckedChange={() => toggleShareSelection(share.token)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{share.hr_email}</span>
                        <Badge variant="outline" className="text-xs">
                          {share.match_score?.toFixed(0) || 0}% match
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {share.job_title} at {share.company_name}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Sent: {new Date(share.created_at).toLocaleDateString()}
                        {share.resend_count > 0 && (
                          <span className="ml-2 text-amber-600">
                            • Resent {share.resend_count}x
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => resendEmail(share.token)}
                      className="shrink-0"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Resend
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareHistory(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
